import { z } from 'zod';
import oemContentContracts from './content-contracts.json';
export { oemContentContracts };
import { oemFieldCatalog } from './field-catalog';
export { oemFieldCatalog };
export const oemKinds = [
  'material',
  'material_spec',
  'ingredient',
  'criterion',
  'component',
  'component_spec',
  'brief',
  'formula',
  'process',
  'density',
  'sample_request',
  'preparation',
  'sample',
  'dispatch',
  'evaluation',
  'target',
  'packaging',
  'sourcing',
  'artwork',
  'annotation',
  'feasibility',
  'applicability',
  'test_plan',
  'test_result',
  'review',
  'hold',
  'policy',
  'cost',
  'quote',
  'po',
  'receipt',
  'allocation',
  'commitment',
  'exception',
  'order',
  'handover',
  'fact',
  'proposal',
  'task',
  'conversation',
  'import_batch',
] as const;
export const oemKindSchema = z.enum(oemKinds);
export const evidenceSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('unknown'), reason: z.string().min(1) }).strict(),
  z
    .object({
      state: z.enum(['proposed', 'estimated']),
      value: z.union([z.string(), z.boolean(), z.array(z.string())]),
      source: z.string().min(1),
    })
    .strict(),
  z
    .object({
      state: z.literal('verified'),
      value: z.union([z.string(), z.boolean(), z.array(z.string())]),
      source: z.string().min(1),
      reviewerId: z.uuid(),
      verifiedAt: z.iso.datetime(),
    })
    .strict(),
  z
    .object({
      state: z.literal('not_applicable'),
      reason: z.string().min(1),
      reviewerId: z.uuid(),
      source: z.string().min(1),
    })
    .strict(),
]);
export const decimalStringSchema = z
  .string()
  .max(50)
  .regex(/^(0|[1-9]\d*)(\.\d+)?$/);
export const oemTargetSchema = z
  .object({
    id: z.uuid(),
    kind: oemKindSchema,
    version: z.int().positive(),
    hash: z.string().regex(/^[a-f0-9]{64}$/),
  })
  .strict();
export const oemCommandSchema = z
  .object({
    idempotencyKey: z.uuid(),
    projectId: z.uuid(),
    skuId: z.uuid().nullable(),
    target: oemTargetSchema.nullable(),
    payload: z.record(z.string(), z.unknown()),
  })
  .strict();
export const oemActions = [
  'create',
  'save_draft',
  'revise',
  'submit',
  'prepare',
  'dispatch',
  'receive',
  'evaluate',
  'decide_sensory',
  'agree_criteria',
  'decide_design',
  'review_technical',
  'review_regulatory',
  'review_commercial',
  'adopt_target',
  'issue_quote',
  'accept_quote',
  'reconcile_po',
  'verify_receipt',
  'allocate_payment',
  'reverse_allocation',
  'authorize_commitment',
  'resolve_hold',
  'confirm_order',
  'pause',
  'cancel',
] as const;
export const oemActionSchema = z.enum(oemActions);
export const oemHandoverAcknowledgementSchema = z
  .object({
    projectId: z.uuid(),
    orderId: z.uuid(),
    orderVersion: z.literal(1),
    orderHash: z.string().regex(/^[a-f0-9]{64}$/),
    idempotencyKey: z.uuid(),
    comment: z.string().min(1).max(4000),
  })
  .strict();
export type OemKind = z.infer<typeof oemKindSchema>;
export type OemCommand = z.infer<typeof oemCommandSchema>;
export type OemTarget = z.infer<typeof oemTargetSchema>;
const text = z.string().max(4000);
const ref = z.uuid();
const refs = z.array(oemTargetSchema).max(200);
const decimal = decimalStringSchema;
const state = z.enum(['unknown', 'proposed', 'estimated', 'verified']);
const line = z
  .object({
    skuId: ref,
    targetId: ref,
    quantity: decimal,
    unitPrice: decimal,
    currency: text,
    tax: decimal,
    specId: ref,
  })
  .strict();
const annotation = z
  .object({
    recordId: ref,
    hash: z.string().regex(/^[a-f0-9]{64}$/),
    page: z.int().positive(),
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    comment: text,
  })
  .strict();
const formulaLine = z
  .object({
    materialId: ref,
    amount: decimal,
    unit: z.enum(['percent', 'kg', 'g', 'L', 'mL']),
    basis: z.enum(['mass', 'volume']),
    supplierSpecId: ref,
    constituents: z.array(
      z
        .object({
          ingredientId: ref,
          concentration: decimal,
          basis: z.enum(['mass', 'volume']),
          source: text,
          state,
        })
        .strict(),
    ),
  })
  .strict();
const costLine = z
  .object({
    source: text,
    state: z.literal('verified'),
    quantity: decimal,
    unitCost: decimal,
    unit: text,
  })
  .strict();
const shared = {
  label: text.optional(),
  summary: text.optional(),
  source: text.optional(),
  revisionReason: text.optional(),
  nextAction: text.optional(),
  dueAt: text.optional(),
};
const make = (shape: Record<string, z.ZodType>) => z.object({ ...shared, ...shape }).strict();
const sourceBundle = z
  .object({
    skuId: ref,
    briefId: ref,
    materialPrices: z.array(z.object({ materialId: ref, priceId: ref }).strict()),
    componentPrices: z.array(z.object({ componentId: ref, priceId: ref }).strict()),
  })
  .strict();
export const oemCostRequestContentSchema = make({
  targetId: ref.optional(),
  policyId: ref.optional(),
  sourceBundles: z.array(sourceBundle).optional(),
});
export const oemCostRequestContract = z.toJSONSchema(oemCostRequestContentSchema);
export const oemContentSchemas: Record<OemKind, z.ZodType> = {
  material: make({ identity: text.optional(), grade: text.optional(), state: state.optional() }),
  ingredient: make({ inci: text.optional(), identity: text.optional(), state: state.optional() }),
  material_spec: make({
    stockSourceRef: z
      .object({ id: ref, version: z.int().positive(), hash: z.string().regex(/^[a-f0-9]{64}$/) })
      .strict()
      .optional(),
    materialId: ref.optional(),
    grade: text.optional(),
    basis: z.enum(['mass', 'volume']).optional(),
    constituents: z
      .array(
        z
          .object({
            ingredientId: ref,
            concentration: decimal,
            basis: z.enum(['mass', 'volume']),
            source: text,
            state,
          })
          .strict(),
      )
      .optional(),
    rightsSource: text.optional(),
    validUntil: text.optional(),
    state: state.optional(),
  }),
  criterion: make({
    code: text.optional(),
    method: text.optional(),
    threshold: text.optional(),
    tolerance: text.optional(),
    audience: z.enum(['customer', 'internal']).optional(),
    state: state.optional(),
  }),
  component: make({
    identity: text.optional(),
    component: text.optional(),
    state: state.optional(),
  }),
  component_spec: make({
    stockSourceRef: z
      .object({ id: ref, version: z.int().positive(), hash: z.string().regex(/^[a-f0-9]{64}$/) })
      .strict()
      .optional(),
    componentId: ref.optional(),
    dimensions: text.optional(),
    material: text.optional(),
    drawingId: ref.optional(),
    rightsSource: text.optional(),
    usagePerSaleUnit: decimal.optional(),
    state: state.optional(),
  }),
  brief: make({
    category: text.optional(),
    market: text.optional(),
    language: text.optional(),
    quantity: decimal.optional(),
    unit: text.optional(),
    fillSize: text.optional(),
    fillUnit: z.enum(['mL', 'L']).optional(),
    intendedUse: text.optional(),
    requestedServices: z.array(text).optional(),
    factoryTargetPrice: decimal.optional(),
    retailTargetPrice: decimal.optional(),
  }),
  formula: make({
    densityState: state.optional(),
    yieldState: state.optional(),
    ipSource: text.optional(),
    permittedUse: text.optional(),
    basis: z.enum(['mass', 'volume']).optional(),
    lines: z.array(formulaLine).optional(),
    processId: ref.optional(),
    densityId: ref.optional(),
    usableYield: decimal.optional(),
  }),
  process: make({
    steps: z
      .array(z.object({ phase: text, instruction: text, equipment: text, source: text }).strict())
      .optional(),
    capacityState: state.optional(),
    cleaningSource: text.optional(),
    roundingPolicy: text.optional(),
  }),
  density: make({
    value: decimal.optional(),
    unit: z.literal('kg/L').optional(),
    method: text.optional(),
    conditions: text.optional(),
    formulaId: ref.optional(),
    state: state.optional(),
  }),
  sample_request: make({
    formulaId: ref.optional(),
    processId: ref.optional(),
    round: z.int().positive().optional(),
    quantity: decimal.optional(),
    termsPolicyId: ref.optional(),
    criteria: z.array(text).optional(),
    criterionIds: z.array(ref).optional(),
  }),
  preparation: make({
    preparationCode: text.optional(),
    formulaId: ref.optional(),
    processId: ref.optional(),
    sampleRequestId: ref.optional(),
    quantity: decimal.optional(),
    unit: text.optional(),
    custodian: text.optional(),
    retainedReference: text.optional(),
    conditions: text.optional(),
  }),
  sample: make({
    sampleCode: text.optional(),
    preparationId: ref.optional(),
    preparationCode: text.optional(),
    formulaId: ref.optional(),
    processId: ref.optional(),
    packagingId: ref.optional(),
    criteria: z.array(text).optional(),
    criterionIds: z.array(ref).optional(),
    illustrative: z.boolean().optional(),
    status: text.optional(),
  }),
  dispatch: make({
    sampleId: ref.optional(),
    dispatchReference: text.optional(),
    recipient: text.optional(),
    receivedAt: text.optional(),
  }),
  evaluation: make({
    sampleId: ref.optional(),
    criteria: z.array(text).optional(),
    ratings: z
      .array(z.object({ criterion: text, rating: z.int().min(1).max(5), comment: text }).strict())
      .optional(),
    comment: text.optional(),
    revisionReason: text.optional(),
  }),
  target: make({
    references: refs.optional(),
    previousTargetId: ref.optional(),
    impactedScopes: z.array(text).optional(),
    commitmentConsequences: text.optional(),
  }),
  packaging: make({
    component: text.optional(),
    material: text.optional(),
    dimensions: text.optional(),
    finish: text.optional(),
    capacity: decimal.optional(),
    fillSize: decimal.optional(),
    drawingId: ref.optional(),
    rightsSource: text.optional(),
    componentIds: z.array(ref).optional(),
    compatibilitySource: text.optional(),
    illustrative: z.boolean().optional(),
  }),
  sourcing: make({
    materialId: ref.optional(),
    unit: z.enum(['kg', 'piece']).optional(),
    componentId: ref.optional(),
    supplierId: ref.optional(),
    price: decimal.optional(),
    currency: text.optional(),
    moq: decimal.optional(),
    multiple: decimal.optional(),
    priceTiers: z
      .array(
        z
          .object({
            minimumQuantity: decimal,
            maximumQuantity: decimal.optional(),
            unitPrice: decimal,
          })
          .strict(),
      )
      .optional(),
    tooling: decimal.optional(),
    leadTime: text.optional(),
    validUntil: text.optional(),
    state: state.optional(),
  }),
  artwork: make({
    fileId: ref.optional(),
    fileStage: z.enum(['editable', 'print_ready', 'proof', 'production']).optional(),
    componentId: ref.optional(),
    dielineId: ref.optional(),
    market: text.optional(),
    language: text.optional(),
    proofPage: z.int().positive().optional(),
    annotations: z.array(annotation).optional(),
    illustrative: z.boolean().optional(),
  }),
  annotation: make({ annotations: z.array(annotation) }),
  feasibility: make({
    capacityState: state.optional(),
    capacitySource: text.optional(),
    equipment: text.optional(),
    batchBasis: text.optional(),
    fillingSource: text.optional(),
    cleaningSource: text.optional(),
    yieldSource: text.optional(),
    outsourcingSource: text.optional(),
  }),
  applicability: make({
    targetIds: z.array(ref).optional(),
    tests: z
      .array(
        z.object({ planId: ref, required: z.boolean(), rationale: text, source: text }).strict(),
      )
      .optional(),
    artworkRequired: z.boolean().optional(),
    depositRequired: z.boolean().optional(),
    rationale: text.optional(),
  }),
  test_plan: make({
    targetIds: z.array(ref).optional(),
    method: text.optional(),
    lab: text.optional(),
    criteria: text.optional(),
    tolerance: text.optional(),
    required: z.boolean().optional(),
  }),
  test_result: make({
    planId: ref.optional(),
    result: z.enum(['pending', 'passed', 'failed']).optional(),
    reportId: ref.optional(),
    value: text.optional(),
  }),
  review: make({
    targetIds: z.array(ref).optional(),
    scope: text.optional(),
    disposition: z.enum(['pending', 'passed', 'failed', 'not_applicable']).optional(),
    rationale: text.optional(),
  }),
  hold: make({
    severity: z.enum(['legal', 'safety', 'quality', 'business']).optional(),
    targetIds: z.array(ref).optional(),
    ownerId: ref.optional(),
    resolutionSource: text.optional(),
  }),
  policy: make({
    policyType: z.enum(['sample', 'commercial', 'finance', 'allowance', 'commitment']).optional(),
    fee: decimal.optional(),
    includedRounds: z.int().nonnegative().optional(),
    extraRoundCharge: decimal.optional(),
    shippingTerms: text.optional(),
    orderCreditTerms: text.optional(),
    depositRequired: z.boolean().optional(),
    depositAmount: decimal.optional(),
    taxPolicy: text.optional(),
    approvedSource: text.optional(),
    commitmentLimit: decimal.optional(),
    commitmentCurrency: text.optional(),
    commitmentRationale: text.optional(),
    tierQuantityBasis: z.literal('planned_purchase').optional(),
    procurementAllocationBasis: z.literal('consumed_quantity').optional(),
    lossBasis: z.enum(['process', 'yield']).optional(),
    loss: decimal.optional(),
    currency: text.optional(),
    tax: decimal.optional(),
    pricingMethod: z.enum(['gross_margin', 'markup']).optional(),
    pricingFraction: decimal.optional(),
    minimumMargin: decimal.optional(),
    priceScale: z.int().min(0).max(6).optional(),
    costScale: z.int().min(0).max(6).optional(),
    allocationBasis: z.literal('saleable_units').optional(),
    charges: z
      .object({
        labor: decimal,
        development: decimal,
        tests: decimal,
        setup: decimal,
        tooling: decimal,
        logistics: decimal,
      })
      .strict()
      .optional(),
    fxRates: z.array(z.object({ currency: text, rate: decimal, source: text }).strict()).optional(),
  }),
  cost: make({
    targetId: ref.optional(),
    policyId: ref.optional(),
    inputs: z.array(costLine).optional(),
    usableYield: decimal.optional(),
    lossBasis: z.enum(['process', 'yield']).optional(),
    loss: decimal.optional(),
    labor: decimal.optional(),
    development: decimal.optional(),
    tests: decimal.optional(),
    setup: decimal.optional(),
    tooling: decimal.optional(),
    logistics: decimal.optional(),
    fx: decimal.optional(),
    tax: decimal.optional(),
    currency: text.optional(),
    engineVersion: z.enum(['oem-cost-v2', 'oem-source-cost-v3', 'oem-source-cost-v4']).optional(),
    sourceBundles: z
      .array(
        z
          .object({
            skuId: ref,
            briefId: ref,
            materialPrices: z.array(z.object({ materialId: ref, priceId: ref }).strict()),
            componentPrices: z.array(z.object({ componentId: ref, priceId: ref }).strict()),
          })
          .strict(),
      )
      .optional(),
    computationState: z.enum(['pending', 'computed']).optional(),
    issues: z.array(text).optional(),
    sourceManifest: z.array(oemTargetSchema).optional(),
    result: z
      .object({
        engineVersion: z.enum(['oem-source-cost-v3', 'oem-source-cost-v4']),
        currency: text,
        tax: decimal,
        rawTotal: decimal,
        roundedTotal: decimal,
        procurements: z
          .array(
            z
              .object({
                sourceId: ref,
                unit: z.enum(['kg', 'piece']),
                consumedQuantity: decimal,
                plannedQuantity: decimal,
                unitPrice: decimal,
                plannedCash: decimal,
                surplusQuantity: decimal,
                allocations: z.array(
                  z
                    .object({
                      skuId: ref,
                      consumedQuantity: decimal,
                      share: decimal,
                      plannedCash: decimal,
                      surplusQuantity: decimal,
                    })
                    .strict(),
                ),
              })
              .strict(),
          )
          .optional(),
        skus: z.array(
          z
            .object({
              skuId: ref,
              quantity: decimal,
              liters: decimal,
              netMass: decimal,
              requiredMass: decimal,
              materials: z.array(
                z.object({ sourceId: ref, quantity: decimal, consumedCost: decimal }).strict(),
              ),
              components: z.array(
                z
                  .object({
                    sourceId: ref,
                    quantity: decimal,
                    consumedCost: decimal,
                    moqCash: decimal,
                    surplusUnits: decimal,
                  })
                  .strict(),
              ),
              sharedCharges: decimal,
              rawTotal: decimal,
              roundedTotal: decimal,
              unitCost: decimal,
              unitPrice: decimal,
              grossMargin: z.string().regex(/^-?\d+(\.\d+)?$/),
            })
            .strict(),
        ),
      })
      .strict()
      .optional(),
    rawTotal: decimal.optional(),
    roundedTotal: decimal.optional(),
    margin: decimal.optional(),
    markup: decimal.optional(),
  }),
  quote: make({
    quoteType: z.enum(['preliminary', 'final']).optional(),
    price: decimal.optional(),
    currency: text.optional(),
    terms: text.optional(),
    validUntil: text.optional(),
    destination: text.optional(),
    lines: z.array(line).optional(),
    targetId: ref.optional(),
    costId: ref.optional(),
    policyId: ref.optional(),
    depositRequired: z.boolean().optional(),
    depositAmount: decimal.optional(),
    capacityId: ref.optional(),
    tax: decimal.optional(),
  }),
  po: make({
    quoteId: ref.optional(),
    lines: z.array(line).optional(),
    terms: text.optional(),
    destination: text.optional(),
    tax: decimal.optional(),
  }),
  receipt: make({
    fileId: ref.optional(),
    amount: decimal.optional(),
    currency: text.optional(),
    bankReference: text.optional(),
    receivedAt: text.optional(),
    paymentStatus: z.enum(['uploaded', 'pending']).optional(),
  }),
  allocation: make({
    receiptId: ref,
    quoteId: ref,
    amount: decimal,
    reversalSource: text.optional(),
  }),
  commitment: make({
    targetId: ref.optional(),
    policyId: ref.optional(),
    currency: text.optional(),
    componentSpecId: ref.optional(),
    componentId: ref.optional(),
    artworkId: ref.optional(),
    quantity: decimal.optional(),
    costCap: decimal.optional(),
    timing: text.optional(),
    ownership: text.optional(),
    surplus: text.optional(),
    cancellationExposure: text.optional(),
  }),
  exception: make({
    policyId: ref.optional(),
    targetId: ref.optional(),
    scope: text.optional(),
    rationale: text.optional(),
    limit: decimal.optional(),
  }),
  order: make({ references: refs }),
  handover: make({ references: refs }),
  fact: make({
    state: state.optional(),
    value: text.optional(),
    reviewerId: ref.optional(),
    validUntil: text.optional(),
  }),
  proposal: make({
    factIds: z.array(ref).optional(),
    targetId: ref.optional(),
    audience: z.literal('customer').optional(),
    sections: z
      .array(z.object({ heading: text, body: text, factIds: z.array(ref) }).strict())
      .optional(),
  }),
  task: make({ assigneeId: ref.optional(), audience: z.enum(['internal', 'customer']).optional() }),
  conversation: make({
    audience: z.enum(['internal', 'customer']).optional(),
    memberIds: z.array(ref).optional(),
  }),
  import_batch: make({
    sourceHash: z
      .string()
      .regex(/^[a-f0-9]{64}$/)
      .optional(),
    sourceVersion: text.optional(),
    rows: z
      .array(
        z
          .object({
            rowId: text,
            sourceIdentity: text,
            canonicalId: ref.nullable(),
            disposition: z.enum(['pending', 'duplicate', 'held', 'eligible']),
            reason: text,
          })
          .strict(),
      )
      .optional(),
  }),
};
export function validateOemContent(kind: OemKind, content: unknown) {
  return oemContentSchemas[kind].safeParse(content);
}
export function validateOemPayload(action: string, payload: unknown) {
  const text = z.string().max(4000);
  const decision = z
    .object({ criteria: z.array(text).max(50).optional(), comment: text.optional() })
    .strict();
  if (action === 'create' || action === 'save_draft' || action === 'revise') {
    const parsed = z
      .object({ kind: oemKindSchema, content: z.unknown() })
      .strict()
      .safeParse(payload);
    if (!parsed.success) return parsed;
    return (
      parsed.data.kind === 'cost'
        ? oemCostRequestContentSchema
        : oemContentSchemas[parsed.data.kind]
    ).safeParse(parsed.data.content);
  }
  if (action === 'resolve_hold')
    return z
      .object({
        comment: text,
        resolutionSource: text,
        evidenceIds: z.array(z.uuid()).min(1).max(20),
      })
      .strict()
      .safeParse(payload);
  if (action === 'prepare')
    return z
      .object({
        preparationCode: text,
        quantity: decimalStringSchema,
        unit: z.enum(['g', 'kg', 'mL', 'L']),
        conditions: text,
        custodian: text,
        retainedReference: text,
      })
      .strict()
      .safeParse(payload);
  if (action === 'dispatch')
    return z.object({ dispatchReference: text, recipient: text }).strict().safeParse(payload);
  if (action === 'allocate_payment')
    return z
      .object({ receiptId: z.uuid(), quoteId: z.uuid(), amount: decimalStringSchema })
      .strict()
      .safeParse(payload);
  if (action === 'reverse_allocation')
    return z
      .object({ allocationId: z.uuid(), source: text, reason: text })
      .strict()
      .safeParse(payload);
  if (action === 'decide_sensory')
    return z
      .object({
        criteria: z.array(text).min(1).max(50),
        comment: text.optional(),
        disposition: z.enum(['accepted', 'partial', 'revision_requested', 'rejected']).optional(),
      })
      .strict()
      .safeParse(payload);
  if (action.startsWith('review_'))
    return z
      .object({
        disposition: z.enum(['passed', 'failed', 'pending', 'not_applicable']),
        comment: text,
      })
      .strict()
      .safeParse(payload);
  return decision.safeParse(payload);
}
export const customerFieldNames = [
  'label',
  'summary',
  'category',
  'market',
  'language',
  'quantity',
  'unit',
  'fillSize',
  'criteria',
  'sampleCode',
  'preparationCode',
  'dispatchReference',
  'receivedAt',
  'ratings',
  'comment',
  'acceptedCriteria',
  'component',
  'dimensions',
  'material',
  'finish',
  'proofPage',
  'annotations',
  'status',
  'price',
  'currency',
  'tax',
  'terms',
  'validUntil',
  'destination',
  'paymentStatus',
  'nextAction',
  'dueAt',
  'illustrative',
  'revisionReason',
  'amount',
  'fileId',
  'fileStage',
] as const;
export function customerProjection(content: Record<string, unknown>): Record<string, unknown> {
  const safe: Record<string, unknown> = {};
  for (const k of customerFieldNames) {
    const v = content[k];
    if (typeof v === 'string' || typeof v === 'boolean' || typeof v === 'number') safe[k] = v;
    else if (k === 'criteria' && Array.isArray(v) && v.every((x) => typeof x === 'string'))
      safe[k] = v;
    else if (k === 'annotations') {
      const parsed = z.array(annotation).safeParse(v);
      if (parsed.success) safe[k] = parsed.data;
    } else if (k === 'ratings') {
      const parsed = z
        .array(z.object({ criterion: text, rating: z.int().min(1).max(5), comment: text }).strict())
        .safeParse(v);
      if (parsed.success) safe[k] = parsed.data;
    }
  }
  return safe;
}
export * from './imports';

export function oemBundle(kind: string) {
  return [
    'material',
    'material_spec',
    'ingredient',
    'formula',
    'process',
    'density',
    'feasibility',
    'applicability',
    'test_plan',
    'test_result',
    'review',
    'hold',
  ].includes(kind)
    ? 'technical'
    : ['sourcing', 'cost', 'policy', 'exception'].includes(kind)
      ? 'cost'
      : [
            'component',
            'component_spec',
            'packaging',
            'artwork',
            'annotation',
            'commitment',
          ].includes(kind)
        ? 'design'
        : ['quote', 'po', 'order', 'handover'].includes(kind)
          ? 'commercial'
          : ['receipt', 'allocation'].includes(kind)
            ? 'finance'
            : 'shared';
}
